<template>
  <section id="contact" class="section">
    <div class="contact__inner">
      <div class="section-header">
        <p class="section-label reveal" ref="el" :class="{ 'is-visible': isVisible }">Contact</p>
        <h2 class="section-title reveal reveal--d1" :class="{ 'is-visible': isVisible }">
          Let's Work Together
        </h2>
        <p class="section-desc reveal reveal--d2" :class="{ 'is-visible': isVisible }">
          Scaling a data platform, modernizing legacy pipelines, or exploring what
          cloud-native architecture looks like for your team? Let's build it.
        </p>
      </div>

      <div class="contact__grid">
        <!-- Left info -->
        <div class="contact__info reveal reveal--left reveal--d2" :class="{ 'is-visible': isVisible }">
          <div class="contact__card card">
            <h3 class="contact__card-title">Get in Touch</h3>
            <div class="contact__methods">
              <a href="https://www.linkedin.com/in/dixit-prajapati" target="_blank" rel="noopener noreferrer" class="contact__method">
                <div class="contact__method-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
                </div>
                <div>
                  <p class="contact__method-label">LinkedIn</p>
                  <p class="contact__method-value">linkedin.com/in/dixit-prajapati</p>
                </div>
              </a>
              <a href="https://github.com/DixitPrajapatii" target="_blank" rel="noopener noreferrer" class="contact__method">
                <div class="contact__method-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0 0 22 12.017C22 6.484 17.522 2 12 2z"/></svg>
                </div>
                <div>
                  <p class="contact__method-label">GitHub</p>
                  <p class="contact__method-value">github.com/DixitPrajapatii</p>
                </div>
              </a>
              <a href="https://medium.com/@dixitofficial98" target="_blank" rel="noopener noreferrer" class="contact__method">
                <div class="contact__method-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zm7.42 0c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z"/></svg>
                </div>
                <div>
                  <p class="contact__method-label">Medium</p>
                  <p class="contact__method-value">@dixitofficial98</p>
                </div>
              </a>
            </div>
          </div>

          <!-- Location card -->
          <div class="contact__location card">
            <div class="contact__location-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </div>
            <div>
              <p class="contact__location-label">Based in</p>
              <p class="contact__location-value">India 🇮🇳</p>
            </div>
            <div class="contact__location-badge">
              <span class="sidebar__badge-dot" style="background:#4ade80"></span>
              Available for Remote Work
            </div>
          </div>
        </div>

        <!-- Right: Form -->
        <div class="contact__form-wrap reveal reveal--right reveal--d3" :class="{ 'is-visible': isVisible }">
          <form class="contact__form card" @submit.prevent="sendEmail">
            <h3 class="contact__form-title">Send a Message</h3>

            <div class="form-row">
              <div class="form-field">
                <label for="from_name">Your Name</label>
                <input
                  id="from_name"
                  type="text"
                  name="from_name"
                  v-model="form.name"
                  placeholder="John Doe"
                  required
                  autocomplete="name"
                  maxlength="100"
                  :disabled="sending"
                />
              </div>
              <div class="form-field">
                <label for="reply_to">Your Email</label>
                <input
                  id="reply_to"
                  type="email"
                  name="reply_to"
                  v-model="form.email"
                  placeholder="john@example.com"
                  required
                  autocomplete="email"
                  maxlength="200"
                  :disabled="sending"
                />
              </div>
            </div>

            <div class="form-field">
              <label for="subject">Subject</label>
              <input
                id="subject"
                type="text"
                name="subject"
                v-model="form.subject"
                placeholder="Let's collaborate on a data project"
                required
                maxlength="200"
                :disabled="sending"
              />
            </div>

            <div class="form-field">
              <label for="message">Message</label>
              <textarea
                id="message"
                name="message"
                v-model="form.message"
                rows="5"
                placeholder="Tell me about your project or opportunity..."
                required
                maxlength="5000"
                :disabled="sending"
              ></textarea>
            </div>

            <!-- Status messages -->
            <Transition name="fade-status">
              <div v-if="status === 'success'" class="form-status form-status--success">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                {{ statusMsg }}
              </div>
              <div v-else-if="status === 'error'" class="form-status form-status--error">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                {{ statusMsg }}
              </div>
            </Transition>

            <button type="submit" class="btn btn--primary form-submit" :disabled="sending">
              <span v-if="!sending">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                Send Message
              </span>
              <span v-else class="form-spinner"></span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </section>

  <!-- Footer -->
  <footer class="footer">
    <div class="footer__inner">
      <p class="footer__copy">
        &copy; {{ new Date().getFullYear() }}
        <span class="gradient-text">Dixit Prajapati</span>.
        All rights reserved.
      </p>
      <div class="footer__links">
        <a href="https://github.com/DixitPrajapatii" target="_blank" rel="noopener noreferrer">GitHub</a>
        <a href="https://www.linkedin.com/in/dixit-prajapati" target="_blank" rel="noopener noreferrer">LinkedIn</a>
        <a href="https://medium.com/@dixitofficial98" target="_blank" rel="noopener noreferrer">Medium</a>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref } from 'vue'
import emailjs from '@emailjs/browser'
import { useReveal } from '../composables/useReveal'

const { el, isVisible } = useReveal()

const form = ref({ name: '', email: '', subject: '', message: '' })
const sending = ref(false)
const status = ref(null)   // 'success' | 'error' | null
const statusMsg = ref('')

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

async function sendEmail() {
  const name    = form.value.name.trim()
  const email   = form.value.email.trim()
  const subject = form.value.subject.trim()
  const message = form.value.message.trim()

  // Client-side validation
  if (name.length < 2) {
    status.value = 'error'
    statusMsg.value = 'Please enter your full name (at least 2 characters).'
    return
  }
  if (!EMAIL_RE.test(email)) {
    status.value = 'error'
    statusMsg.value = 'Please enter a valid email address.'
    return
  }
  if (subject.length < 3) {
    status.value = 'error'
    statusMsg.value = 'Please enter a subject (at least 3 characters).'
    return
  }
  if (message.length < 10) {
    status.value = 'error'
    statusMsg.value = 'Please enter a message (at least 10 characters).'
    return
  }

  sending.value = true
  status.value = null
  statusMsg.value = ''

  try {
    await emailjs.send(
      'service_znxd10g',
      'template_rhnwi38',
      { from_name: name, reply_to: email, subject, message },
      { publicKey: 'fEWKNG7l_xikVFJYF' }
    )
    status.value = 'success'
    statusMsg.value = "Message sent! I'll get back to you soon."
    form.value = { name: '', email: '', subject: '', message: '' }
  } catch (err) {
    console.error('[EmailJS] send failed:', err)
    status.value = 'error'
    statusMsg.value = 'Something went wrong. Please try again or reach out via LinkedIn.'
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
.contact__inner { max-width: 1200px; margin: 0 auto; }

.contact__grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 32px;
  align-items: start;
}

/* Info column */
.contact__card { padding: 16px; margin-bottom: 10px; }
.contact__card-title {
  font-size: 14px; font-weight: 700;
  color: var(--color-text);
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border);
}

.contact__methods { display: flex; flex-direction: column; gap: 4px; }
.contact__method {
  display: flex; align-items: center; gap: 10px;
  padding: 8px;
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  transition: var(--transition);
}
.contact__method:hover {
  background: rgba(var(--color-accent-rgb), 0.06);
  color: var(--color-accent);
}
.contact__method-icon {
  width: 36px; height: 36px;
  display: flex; align-items: center; justify-content: center;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xs);
  color: var(--color-accent);
  flex-shrink: 0;
  transition: var(--transition);
}
.contact__method:hover .contact__method-icon {
  border-color: var(--color-border-accent);
  background: rgba(var(--color-accent-rgb), 0.1);
}
.contact__method-label {
  font-size: 10px; font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase; letter-spacing: 0.1em;
  font-family: var(--font-mono);
}
.contact__method-value { font-size: 13px; font-weight: 500; }

.contact__location {
  padding: 16px 20px;
  display: flex; align-items: center; gap: 12px;
  flex-wrap: wrap;
}
.contact__location-icon {
  width: 36px; height: 36px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(var(--color-accent-rgb), 0.08);
  border: 1px solid rgba(var(--color-accent-rgb), 0.2);
  border-radius: var(--radius-xs);
  color: var(--color-accent);
  flex-shrink: 0;
}
.contact__location-label {
  font-size: 10px; font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase; letter-spacing: 0.1em;
  font-family: var(--font-mono);
}
.contact__location-value { font-size: 14px; font-weight: 600; color: var(--color-text); }
.contact__location-badge {
  margin-left: auto;
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 500;
  color: #4ade80;
}

/* Form */
.contact__form { padding: 20px; }
.contact__form-title {
  font-size: 15px; font-weight: 700;
  color: var(--color-text);
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-border);
}

.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.form-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 10px; }

label {
  font-size: 11px; font-weight: 600;
  color: var(--color-text-secondary);
  letter-spacing: 0.05em;
}

input, textarea {
  background: rgba(255,255,255,0.04);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 8px 12px;
  font-size: 13px;
  color: var(--color-text);
  font-family: var(--font-sans);
  transition: var(--transition);
  outline: none;
  width: 100%;
}
input::placeholder, textarea::placeholder { color: var(--color-text-muted); }
input:focus, textarea:focus {
  border-color: var(--color-accent);
  background: rgba(var(--color-accent-rgb), 0.04);
  box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
}
input:disabled, textarea:disabled { opacity: 0.5; cursor: not-allowed; }
textarea { resize: vertical; min-height: 80px; }

.form-status {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px;
  border-radius: var(--radius-sm);
  font-size: 13px; font-weight: 500;
  margin-bottom: 16px;
}
.form-status--success {
  background: rgba(74, 222, 128, 0.1);
  border: 1px solid rgba(74, 222, 128, 0.3);
  color: #4ade80;
}
.form-status--error {
  background: rgba(248, 113, 113, 0.1);
  border: 1px solid rgba(248, 113, 113, 0.3);
  color: #f87171;
}

.form-submit { width: 100%; justify-content: center; height: 40px; }
.form-submit:disabled { opacity: 0.6; cursor: not-allowed; transform: none !important; }

.form-spinner {
  width: 18px; height: 18px;
  border: 2px solid rgba(0,0,0,0.3);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: block;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Footer */
.footer {
  background: var(--color-bg-secondary);
  border-top: 1px solid var(--color-border);
  padding: 24px 60px;
}
.footer__inner {
  max-width: 1200px; margin: 0 auto;
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; flex-wrap: wrap;
}
.footer__copy { font-size: 13px; color: var(--color-text-muted); }
.footer__links { display: flex; gap: 20px; }
.footer__links a {
  font-size: 13px; color: var(--color-text-muted);
  transition: var(--transition);
}
.footer__links a:hover { color: var(--color-accent); }

/* Sidebar badge dot reuse */
.sidebar__badge-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
  display: inline-block;
}
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }

/* Status transition */
.fade-status-enter-active, .fade-status-leave-active { transition: all 0.3s ease; }
.fade-status-enter-from, .fade-status-leave-to { opacity: 0; transform: translateY(-8px); }

@media (max-width: 900px) {
  .contact__grid { grid-template-columns: 1fr; gap: 20px; }
  .form-row { grid-template-columns: 1fr; }
  .footer { padding: 20px 24px; }
}
@media (max-width: 768px) {
  .contact__form { padding: 20px; }
  .contact__card { padding: 18px; }
  .contact__form-title { font-size: 16px; margin-bottom: 18px; padding-bottom: 12px; }
  .contact__location { flex-wrap: wrap; gap: 10px; }
  .contact__location-badge { width: 100%; margin-left: 0; }
  .footer__inner { flex-direction: column; align-items: center; text-align: center; gap: 10px; }
}
@media (max-width: 480px) {
  .contact__form { padding: 16px; }
  .contact__card { padding: 14px; }
  input, textarea { padding: 10px 12px; font-size: 13px; }
  .form-submit { height: 44px; }
  .contact__method { padding: 8px; gap: 10px; }
  .contact__method-icon { width: 32px; height: 32px; }
  .footer__links { gap: 14px; }
  .footer__links a { font-size: 12px; }
}
</style>
